import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveErrorMessage } from '@kidshome/api-contract';
import { PHOTO_PROOF_MAX_BYTES_DEFAULT } from '@kidshome/shared';
import { Button } from '@kidshome/ui';
import { ActionSheet } from '@/shared/ui';
import { tDynamic } from '@/shared/lib/i18n';
import {
  processPhotoProofFile,
  PhotoProofImageProcessingError,
  type PhotoProofImageError,
} from '@/features/upload-photo-proof/photo-proof-image';
import {
  usePhotoCompleteTaskAction,
  type PhotoCompleteContext,
} from '@/features/photo-complete-task/use-photo-complete-task';

const MAX_BYTES = PHOTO_PROOF_MAX_BYTES_DEFAULT;

const PROCESSING_ERROR_KEY: Record<PhotoProofImageError, string> = {
  'decode-failed': 'photoProof.decodeFailed',
  'encode-failed': 'photoProof.encodeFailed',
};

type Stage = { kind: 'idle' } | { kind: 'preview'; blob: Blob; previewUrl: string };

/**
 * The camera control for a VIRTUAL photo-proof card (ADR-0142 F8): a round camera button (matching the
 * day board's `.check cam`) that opens a capture sheet. The picked image is processed entirely
 * client-side (downscale + WebP re-encode, stripping EXIF/GPS — same pipeline as `PhotoCapture`), then
 * completed in ONE multipart step via `usePhotoCompleteTaskAction` (there is no occurrence id to upload
 * against yet). On success the sheet closes and the day/missions caches reconcile from the server.
 */
export function PhotoCompleteButton({
  card,
  ctx,
}: {
  card: { templateId: string; scheduledLocalDate: string; assigneeId: string; title: string };
  ctx: PhotoCompleteContext;
}): React.ReactElement {
  const { t } = useTranslation('family');
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  const [validationError, setValidationError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const { submit, isPending, error } = usePhotoCompleteTaskAction(ctx);

  useEffect(() => {
    const url = stage.kind === 'preview' ? stage.previewUrl : null;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [stage]);

  const reset = (): void => {
    setStage({ kind: 'idle' });
    setValidationError(null);
  };

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    setValidationError(null);
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setValidationError(t('photoProof.wrongType'));
      return;
    }
    if (file.size > MAX_BYTES) {
      setValidationError(t('photoProof.tooLarge'));
      return;
    }
    setProcessing(true);
    try {
      const blob = await processPhotoProofFile(file);
      setStage({ kind: 'preview', blob, previewUrl: URL.createObjectURL(blob) });
    } catch (err) {
      const reason = err instanceof PhotoProofImageProcessingError ? err.reason : 'decode-failed';
      setValidationError(tDynamic(t, PROCESSING_ERROR_KEY[reason]));
    } finally {
      setProcessing(false);
    }
  };

  const onConfirm = (): void => {
    if (stage.kind !== 'preview') return;
    const file = new File([stage.blob], 'photo-proof.webp', { type: 'image/webp' });
    void submit(card, file).then(
      () => {
        reset();
        setOpen(false);
      },
      () => {
        // The error surfaces below; keep the sheet open so the kid can retake or retry.
      },
    );
  };

  const errorMessage =
    error != null
      ? (() => {
          const m = resolveErrorMessage(error);
          return tDynamic(t, m.key, m.params);
        })()
      : null;

  return (
    <>
      <button
        type="button"
        className="check cam"
        onClick={() => setOpen(true)}
        aria-label={t('photoProof.addPhotoAriaLabel', { chore: card.title })}
        title={t('photoProof.addPhoto')}
      >
        {/* camera glyph */}
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="size-5">
          <path
            d="M4 8h3l1.5-2h7L17 8h3v11H4V8z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <circle cx="12" cy="13" r="3.2" stroke="currentColor" strokeWidth="2" />
        </svg>
      </button>
      <ActionSheet
        open={open}
        onClose={() => {
          reset();
          setOpen(false);
        }}
        title={t('photoProof.addPhoto')}
      >
        <div className="flex flex-col gap-3">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            disabled={processing || isPending}
            onChange={(e) => void onPick(e)}
          />
          {stage.kind === 'idle' ? (
            <Button
              variant="outline"
              size="sm"
              disabled={processing}
              onClick={() => inputRef.current?.click()}
            >
              {processing ? t('photoProof.processing') : t('photoProof.addPhoto')}
            </Button>
          ) : (
            <div className="flex flex-col gap-3">
              <img
                src={stage.previewUrl}
                alt={t('photoProof.previewAlt')}
                className="max-h-64 w-full rounded-card bg-surface-muted object-contain"
              />
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  className="flex-1"
                  disabled={isPending}
                  onClick={onConfirm}
                >
                  {isPending ? t('photoProof.uploading') : t('photoProof.usePhoto')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isPending}
                  onClick={() => {
                    reset();
                    inputRef.current?.click();
                  }}
                >
                  {t('photoProof.retake')}
                </Button>
              </div>
            </div>
          )}
          {validationError && (
            <p role="alert" className="text-sm font-semibold text-danger">
              {validationError}
            </p>
          )}
          {errorMessage && (
            <p role="alert" className="text-sm font-semibold text-danger">
              {errorMessage}
            </p>
          )}
        </div>
      </ActionSheet>
    </>
  );
}
